<template>
  <div class="container py-4">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <button class="btn btn-secondary" @click="navigateBack">
            <i class="bi bi-arrow-left"></i> Terug
          </button>
          <h4 class="d-inline-block ms-3">{{ isNewDebtor ? 'Nieuwe debiteur' : 'Debiteur bewerken' }}</h4>
        </div>
        <div v-if="!isNewDebtor">
          <button class="btn btn-danger" @click="confirmDelete">
            <i class="bi bi-trash"></i> Verwijderen
          </button>
        </div>
      </div>

      <div class="card-body">
        <form @submit.prevent="saveDebtor">
          <div class="mb-3">
            <label for="debtorName" class="form-label">Naam *</label>
            <input
              type="text"
              class="form-control"
              id="debtorName"
              v-model="editingDebtor.name"
              required
              placeholder="Naam van de debiteur"
            />
          </div>

          <div class="mb-3">
            <label for="addressId" class="form-label">Address ID</label>
            <input
              type="text"
              class="form-control"
              id="addressId"
              v-model="editingDebtor.address_id"
              placeholder="Address ID"
            />
          </div>

          <div class="mb-3">
            <label for="organisationCode" class="form-label">Organisatie Code</label>
            <input
              type="text"
              class="form-control"
              id="organisationCode"
              v-model="editingDebtor.organisation_code"
              placeholder="Organisatie Code"
            />
          </div>

          <div class="mb-3">
            <label for="debtorCode" class="form-label">Debiteur Code</label>
            <input
              type="text"
              class="form-control"
              id="debtorCode"
              v-model="editingDebtor.debtor_code"
              placeholder="Debiteur Code"
            />
          </div>

          <div class="mb-3">
            <label for="portal_url" class="form-label">Klantportaal URL</label>
            <input
              type="url"
              class="form-control"
              id="portal_url"
              v-model="editingDebtor.portal_url"
              placeholder="https://..."
            />
            <small class="form-text text-muted">Link naar het klantportaal waar de klant orders kan plaatsen</small>
            <div v-if="editingDebtor.portal_url" class="mt-2">
              <a v-if="isValidUrl(editingDebtor.portal_url)" 
                 :href="editingDebtor.portal_url" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 class="btn btn-sm btn-outline-primary">
                <i class="bi bi-box-arrow-up-right me-1"></i>Klantportaal openen
              </a>
              <div v-else class="text-warning small">
                <i class="bi bi-exclamation-triangle me-1"></i>Ongeldige URL
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end mt-4">
            <button type="button" class="btn btn-secondary me-2" @click="navigateBack">
              Annuleren
            </button>
            <button type="submit" class="btn btn-primary">
              Opslaan
            </button>
          </div>
          
          <!-- E-mails met Collapse -->
          <div class="mt-5 mb-4">
            <button type="button" 
                    class="btn btn-link text-start d-flex justify-content-between align-items-center w-100 p-0 border-0"
                    @click="toggleEmailCollapse"
                    :aria-expanded="!isEmailCollapsed"
                    aria-controls="emailCollapse"
                    :aria-label="`${isEmailCollapsed ? 'Expand' : 'Collapse'} email section`">
              <h4 class="mb-0">E-mail</h4>
              <span class="btn btn-sm btn-info">
                <i class="bi" :class="isEmailCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'"></i>
              </span>
            </button>
            <div class="collapse" :class="{ 'show': !isEmailCollapsed }" id="emailCollapse">
              <div class="mt-3">
                <EmailManager 
                  v-model="editingDebtor.emails" 
                  @save="handleEmailSave"
                  @delete="handleEmailDelete"
                  @error="handleEmailError"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirmation modal met data-attributen -->
    <div class="modal fade" id="deleteDebtorModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteModalLabel">Debiteur verwijderen</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>Weet u zeker dat u deze debiteur wilt verwijderen?</p>
            <p class="text-danger">Deze actie kan niet ongedaan worden gemaakt.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Annuleren</button>
            <button type="button" class="btn btn-danger" @click="deleteDebtor" data-bs-dismiss="modal">Verwijderen</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Geen lokale Toast component meer, we gebruiken emit -->
  </div>
</template>

<script>
import * as bootstrap from 'bootstrap';
import DebtorService from '../services/DebtorService';
import EmailManager from '../components/EmailManager.vue';

export default {
  name: 'DebtorPage',
  components: {
    EmailManager
  },
  emits: ['showToast'],
  props: {
    customerId: {
      type: [Number, String],
      required: true
    },
    debtorId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      // Geen deleteModal reference meer nodig
      isEmailCollapsed: localStorage.getItem('isDebtorEmailCollapsed') === 'false' ? false : true,
      emailCategories: ['success', 'notification', 'error', 'admin', 'error_invoice'],
      editingDebtor: {
        addressId: 0,
        name: '',
        debtorCode: '',
        organisationCode: '',
        portal_url: '',
        customer_id: parseInt(this.customerId),
        emails: {}
      },
      isLoading: false
    };
  },
  computed: {
    isNewDebtor() {
      return this.debtorId === 'new' || !this.debtorId;
    }
  },
  async created() {
    this.debtorService = new DebtorService();
    
    // Zet de customer ID
    this.editingDebtor.customer_id = parseInt(this.customerId);
    
    // Haal debiteur op als het geen nieuwe is
    if (!this.isNewDebtor) {
      this.isLoading = true;
      try {
        const response = await this.debtorService.getDebtor(this.debtorId);
        this.editingDebtor = response.data;
        
        // Initialiseer email structuur
        this.initializeEmailsStructure();
      } catch (error) {
        this.showToast('Fout bij het ophalen van debiteurgegevens', 'bg-danger');
      } finally {
        this.isLoading = false;
      }
    } else {
      // Ook voor nieuwe debiteuren email structuur initialiseren
      this.initializeEmailsStructure();
    }
  },
  // geen mounted functie meer nodig voor modal initialisatie
  methods: {
    async saveDebtor() {
      if (!this.editingDebtor.name) {
        this.showToast('Naam is verplicht voor een debiteur', 'bg-warning');
        return;
      }

      try {
        if (this.isNewDebtor) {
          // Nieuwe debiteur aanmaken
          await this.debtorService.createDebtor(this.editingDebtor);
          this.showToast('Debiteur succesvol toegevoegd', 'bg-success');
        } else {
          // Bestaande debiteur bijwerken
          await this.debtorService.updateDebtor(this.debtorId, this.editingDebtor);
          this.showToast('Debiteur succesvol bijgewerkt', 'bg-success');
        }
        
        // Na het opslaan, kort wachten en dan teruggaan
        setTimeout(() => {
          this.navigateBack();
        }, 1500);
      } catch (error) {
        console.error('Fout bij het opslaan van debiteur:', error);
        this.showToast('Fout bij het opslaan van debiteur', 'bg-danger');
      }
    },
    confirmDelete() {
      // Gebruik bootstrap Modal op declaratieve wijze
      const deleteModal = new bootstrap.Modal(document.getElementById('deleteDebtorModal'));
      deleteModal.show();
    },
    async deleteDebtor() {
      try {
        await this.debtorService.deleteDebtor(this.debtorId);
        this.showToast('Debiteur succesvol verwijderd', 'bg-success');
        
        // Na verwijderen kort wachten en dan teruggaan
        setTimeout(() => {
          this.navigateBack();
        }, 1500);
      } catch (error) {
        console.error('Fout bij het verwijderen van debiteur:', error);
        this.showToast('Fout bij het verwijderen van debiteur', 'bg-danger');
      }
    },
    navigateBack() {
      // Navigeer terug naar de CustomerPage met dezelfde geselecteerde customer
      this.$router.push(`/customers?id=${this.customerId}`);
    },
    showToast(message, bgClass) {
      this.$emit('showToast', [message, bgClass]);
    },
    
    // Email functies
    toggleEmailCollapse() {
      this.isEmailCollapsed = !this.isEmailCollapsed;
      localStorage.setItem('isDebtorEmailCollapsed', this.isEmailCollapsed);
    },
    
    async handleEmailSave(data) {
      const { category, email } = data;
      try {
        // Werk de emails bij
        const updatedEmails = { ...this.editingDebtor.emails };
        if (!updatedEmails[category]) {
          updatedEmails[category] = [];
        }
        
        // Voeg toe of update
        const existingIndex = updatedEmails[category].findIndex(e => e.email === email.email);
        if (existingIndex === -1) {
          updatedEmails[category].push(email);
        } else {
          updatedEmails[category][existingIndex] = email;
        }
        
        this.editingDebtor.emails = updatedEmails;
        
        // Sla op via de speciale email update route
        await this.debtorService.updateDebtorEmails(this.debtorId, this.editingDebtor.emails);
        this.showToast('E-mail succesvol opgeslagen', 'bg-success');
      } catch (error) {
        console.error('Error saving email', error);
        const messageList = [
          'Er is een fout opgetreden bij het opslaan van de e-mail.',
          error.response?.data.message ? error.response.data.message : ''
        ];
        this.showToast(messageList.join(' '), 'bg-danger');
      }
    },
    
    async handleEmailDelete() {
      try {
        // Gebruik de speciale email update route
        await this.debtorService.updateDebtorEmails(this.debtorId, this.editingDebtor.emails);
        this.showToast('E-mail succesvol verwijderd', 'bg-success');
      } catch (error) {
        this.showToast('Er is een fout opgetreden bij het verwijderen van de e-mail', 'bg-danger');
      }
    },
    
    handleEmailError(message) {
      this.showToast(message, 'bg-danger');
    },
    
    /**
     * Initialiseer de email structuur zodat alle categorieën bestaan en arrays zijn
     * Handelt ook het geval af waarin emails als array is geïnitialiseerd
     */
    initializeEmailsStructure() {
      // Als emails nog niet bestaat of een array is (in plaats van een object), maak er een object van
      if (!this.editingDebtor.emails || Array.isArray(this.editingDebtor.emails)) {
        this.editingDebtor.emails = {};
      }
      
      // Zorg ervoor dat elke categorie een array is
      this.emailCategories.forEach(category => {
        if (!this.editingDebtor.emails[category]) {
          this.editingDebtor.emails[category] = [];
        }
      });
    },
    isValidUrl(string) {
      if (!string) return false;
      try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch (_) {
        return false;
      }
    },
  }
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

.toast-header {
  color: white;
}
</style>
